import type { Scene } from "@babylonjs/core/scene";
import { Ray } from "@babylonjs/core/Culling/ray";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import type { LinesMesh } from "@babylonjs/core/Meshes/linesMesh";
import type { WebXRDefaultExperience } from "@babylonjs/core/XR/webXRDefaultExperience";
import type { WebXRInputSource } from "@babylonjs/core/XR/webXRInputSource";
import type { Rig } from "./rig";
import type { Terrain } from "../render/terrain";
import { AimReticle } from "./aim";
import { TextPanel } from "../render/panel";
import type { SeedPouch, Planter } from "./planting";
import { SPECIES } from "../sim/species";
import { reportError } from "../diagnostics";

const VALID = new Color3(0.4, 0.9, 0.5);
const INVALID = new Color3(0.9, 0.35, 0.3);

/**
 * Quest controller mapping (v1, hand tracking is out of scope):
 *   dominant trigger → teleport (blink fade)
 *   dominant grip    → plant selected species
 *   non-dominant thumbstick X → cycle the seed pouch
 *
 * Visual aids so the mapping is discoverable in-headset: a laser from the aiming
 * hand, the teleport/plant ground reticle, and a wrist panel on the off hand
 * showing the selected seed and the controls. Every input handler is wrapped so
 * a thrown error surfaces on the in-scene error panel instead of silently
 * killing the session.
 */
export function setupControllers(
  scene: Scene,
  xr: WebXRDefaultExperience,
  rig: Rig,
  terrain: Terrain,
  pouch: SeedPouch,
  planter: Planter,
  dominantHandedness: "left" | "right" = "right",
): void {
  // Teleport aims with a parabolic arc (the natural "lob to a spot" read);
  // planting aims with a straight laser for precise placement. Both land on one
  // disc, so there's a single unambiguous target either way.
  const teleReticle = new AimReticle(scene, "xrTele", VALID, true);
  const plantReticle = new AimReticle(scene, "xrPlant", new Color3(0.9, 0.8, 0.4), false);
  let dominant: WebXRInputSource | null = null;
  let gripHeld = false;
  const ray = new Ray(Vector3.Zero(), new Vector3(0, 0, 1), 60);
  const laser = new Laser(scene);
  const seedTint = new Color3();
  let wrist: TextPanel | null = null;

  const guard = (label: string, fn: () => void): void => {
    try {
      fn();
    } catch (e) {
      reportError(label, e);
    }
  };

  xr.input.onControllerAddedObservable.add((controller) => {
    controller.onMotionControllerInitObservable.add((mc) => {
      const isDominant = mc.handedness === dominantHandedness;
      if (isDominant) dominant = controller;

      if (isDominant) {
        const trigger = mc.getComponentOfType("trigger");
        trigger?.onButtonStateChangedObservable.add((c) => {
          if (!c.pressed || c.value <= 0.6) return;
          guard("teleport", () => {
            const hit = aim();
            if (hit && hit.valid) rig.teleport(hit.x, hit.z);
          });
        });

        const grip = mc.getComponentOfType("squeeze");
        grip?.onButtonStateChangedObservable.add((c) => {
          const pressed = c.pressed && c.value > 0.6;
          const wasHeld = gripHeld;
          gripHeld = pressed;
          if (pressed && !wasHeld) {
            guard("plant", () => {
              const hit = aim();
              if (hit && hit.valid) planter.plant(pouch.species, hit.x, hit.z);
            });
          }
        });
        // A button (or X on a left dominant hand) cycles the selected seed.
        const face = mc.getComponent("a-button") ?? mc.getComponent("x-button");
        let faceArmed = true;
        face?.onButtonStateChangedObservable.add((c) => {
          if (!c.pressed) faceArmed = true;
          else if (faceArmed) {
            faceArmed = false;
            guard("cycle-seed", () => pouch.cycle(1));
          }
        });

        // The wrist "watch" rides the aiming (dominant) hand — glance at it like a watch.
        if (!wrist) wrist = makeWristPanel(scene, controller, pouch);
      } else {
        // Non-dominant hand: thumbstick cycles the selected species.
        const stick = mc.getComponentOfType("thumbstick");
        let armed = true;
        stick?.onAxisValueChangedObservable.add((axes) => {
          if (Math.abs(axes.x) < 0.5) armed = true;
          else if (armed) {
            guard("cycle-seed", () => pouch.cycle(axes.x > 0 ? 1 : -1));
            armed = false;
          }
        });
      }
    });
    controller.onDisposeObservable.add(() => {
      if (dominant === controller) dominant = null;
    });
  });

  const aim = (): { x: number; z: number; valid: boolean } | null => {
    if (!dominant?.pointer) return null;
    // Use the pointer (targetRay) pose, NOT the grip: `getWorldPointerRayToRef`'s
    // second arg is `gripIfAvailable` — passing it makes the ray follow the
    // controller's handle axis, which sits well off from where you think you're
    // pointing (the old "teleport lands at an angle" bug).
    dominant.getWorldPointerRayToRef(ray);
    const hit = scene.pickWithRay(ray, (m) => m === terrain.mesh);
    if (!hit?.pickedPoint) return null;
    const p = hit.pickedPoint;
    return { x: p.x, z: p.z, valid: terrain.isValidTarget(p.x, p.z) };
  };

  scene.onBeforeRenderObservable.add(() => {
    teleReticle.hide();
    plantReticle.hide();
    laser.hide();
    if (!dominant?.pointer || rig.isBusy) return;
    guard("aim-render", () => {
      const from = dominant!.pointer!.absolutePosition;
      const hit = aim();
      if (!hit) {
        // No ground hit: still show a short laser so the hand is locatable.
        laser.show(from, from.add(ray.direction.scale(6)), false);
        return;
      }
      const target = new Vector3(hit.x, terrain.heightAt(hit.x, hit.z), hit.z);
      if (gripHeld) {
        const [sr, sg, sb] = SPECIES[pouch.species].color;
        seedTint.set(sr, sg, sb);
        plantReticle.setTint(seedTint); // the disc shows the seed you're about to plant
        laser.show(from, target, hit.valid); // straight beam for precise placement
        plantReticle.show(from, target, hit.valid);
      } else {
        teleReticle.show(from, target, hit.valid); // parabolic arc for teleport
      }
    });
  });
}

/** A straight beam from the aiming hand to the target, coloured by validity. */
class Laser {
  private line: LinesMesh | null = null;
  constructor(private readonly scene: Scene) {}
  show(from: Vector3, to: Vector3, valid: boolean): void {
    const pts = [from, to];
    this.line = this.line
      ? CreateLines("xrLaser", { points: pts, instance: this.line, updatable: true }, this.scene)
      : CreateLines("xrLaser", { points: pts, updatable: true }, this.scene);
    this.line.color = valid ? VALID : INVALID;
    this.line.isPickable = false;
    this.line.renderingGroupId = 2;
    this.line.setEnabled(true);
  }
  hide(): void {
    this.line?.setEnabled(false);
  }
}

/**
 * Wrist "watch" on the dominant (aiming) hand: the selected seed + a short
 * control legend. Parented to the grip and tilted to face up-and-back toward the
 * player's eyes, like glancing at a wristwatch. (The exact grip-space offsets are
 * a sensible default; they may want a small on-device nudge.)
 */
function makeWristPanel(scene: Scene, controller: WebXRInputSource, pouch: SeedPouch): TextPanel {
  const panel = new TextPanel(scene, "wrist", 0.16, 0.16);
  panel.mesh.parent = controller.grip ?? controller.pointer;
  panel.mesh.position.set(0, 0.02, 0.05); // above the wrist, back toward the forearm
  // The panel's readable face is -Z; a +90°-ish pitch turns that face up out of
  // the wrist (back of the hand), so a natural wrist-turn brings it to the eyes —
  // and it reads the right way round (negative pitch showed the mirrored back).
  panel.mesh.rotation.set(1.3, 0, 0);
  const render = (id: string): void => {
    const [r, g, b] = SPECIES[id as keyof typeof SPECIES].color;
    const col = `rgb(${(r * 255) | 0},${(g * 255) | 0},${(b * 255) | 0})`;
    panel.setText(
      [
        { text: "PLANTING", fontPx: 20, color: "#b9ae98", align: "center" },
        { text: "●", fontPx: 96, color: col, align: "center", gapBefore: 2 },
        { text: id, fontPx: 42, color: "#ffffff", align: "center", gapBefore: 0 },
        { text: "A or stick — change", fontPx: 20, color: "#c8bfa8", align: "center", gapBefore: 12 },
      ],
      { bg: "rgba(18,14,10,0.92)", pad: 14 },
    );
  };
  pouch.onChange(render);
  return panel;
}
